import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Trash2, Loader2 } from 'lucide-react';
import { apiService, ChatResponse, ServerChatMessage } from '../services/api';
import { getAgentLanguage } from '../utils/agentLanguage';
import ChatMarkdown from './ChatMarkdown';

type ChatMsg = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  time: string;
};

type ChatMessagesUpdater = ChatMsg[] | ((prev: ChatMsg[]) => ChatMsg[]);

interface ChatPanelProps {
  sessionId?: string | null;
  agentId?: string | null;
  onChatMessage?: (message: string, options?: { onToken?: (chunk: string) => void }) => Promise<ChatResponse>;
  isBusy?: boolean;
  externalEvents?: { id: string; role: 'user' | 'assistant'; content: string; time?: string }[];
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  sessionId,
  agentId,
  onChatMessage,
}) => {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const welcomeMessage = `Welcome! I can help you with your ${getAgentLanguage(agentId).analysisPhrase}. How can I assist you today?`;
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>(() => [
    {
      id: 'greeting',
      role: 'assistant',
      content: welcomeMessage,
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    },
  ]);

  const lastSessionIdRef = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const chatMessagesRef = useRef<ChatMsg[]>(chatMessages);
  const currentAssistantIdRef = useRef<string | null>(null);
  const localMessageCounterRef = useRef(1);
  const sendingRef = useRef(false);
  const stickToBottomRef = useRef(true);
  const pendingScrollFrameRef = useRef<number | null>(null);
  const pendingStreamFrameRef = useRef<number | null>(null);
  const streamedContentRef = useRef('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const buildGreeting = useCallback(
    (content: string): ChatMsg => ({
      id: `greeting-${Date.now()}`,
      role: 'assistant',
      content,
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    }),
    []
  );

  const createLocalMessageId = useCallback((): string => {
    const nextId = localMessageCounterRef.current;
    localMessageCounterRef.current += 1;
    return `local-${nextId}`;
  }, []);

  const setMessages = useCallback((updater: ChatMessagesUpdater) => {
    setChatMessages((prev) => {
      const next = typeof updater === 'function'
        ? (updater as (prevValue: ChatMsg[]) => ChatMsg[])(prev)
        : updater;
      chatMessagesRef.current = next;
      return next;
    });
  }, []);

  const scheduleScrollToBottom = useCallback((force = false) => {
    if (pendingScrollFrameRef.current !== null) {
      return;
    }
    pendingScrollFrameRef.current = window.requestAnimationFrame(() => {
      pendingScrollFrameRef.current = null;
      const container = listRef.current;
      if (!container) {
        return;
      }
      if (!force && !stickToBottomRef.current) {
        return;
      }
      container.scrollTop = container.scrollHeight;
    });
  }, []);

  const setAssistantContent = useCallback(
    (assistantId: string, content: string) => {
      setMessages((prev) => prev.map((msg) => (msg.id === assistantId ? { ...msg, content } : msg)));
      scheduleScrollToBottom(false);
    },
    [scheduleScrollToBottom, setMessages]
  );

  const flushStreamedContent = useCallback(
    (assistantId: string) => {
      if (pendingStreamFrameRef.current !== null) {
        return;
      }
      pendingStreamFrameRef.current = window.requestAnimationFrame(() => {
        pendingStreamFrameRef.current = null;
        setAssistantContent(assistantId, streamedContentRef.current);
      });
    },
    [setAssistantContent]
  );

  useEffect(() => {
    const loadMessages = async () => {
      try {
        const nextSessionId = sessionId || null;
        const prevSessionId = lastSessionIdRef.current;
        lastSessionIdRef.current = nextSessionId;
        const hasLocalConversation = chatMessagesRef.current.some((entry) => entry.role === 'user');
        if (prevSessionId === null && nextSessionId && hasLocalConversation) {
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        const serverMessages = await apiService.getChatMessages(sessionId || undefined);
        if (serverMessages.length > 0) {
          const orderedMessages = [...serverMessages].sort((a: ServerChatMessage, b: ServerChatMessage) => {
            const t1 = new Date(a.created_at).getTime();
            const t2 = new Date(b.created_at).getTime();
            if (t1 !== t2) {
              return t1 - t2;
            }
            if (a.role !== b.role) {
              return a.role === 'user' ? -1 : 1;
            }
            return String(a.id).localeCompare(String(b.id));
          });
          const converted = orderedMessages.map((entry, idx) => ({
            id: entry.id ? `server-${entry.id}` : `server-${idx + 1}`,
            role: entry.role as 'user' | 'assistant',
            content: entry.content,
            time: new Date(entry.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          }));
          setMessages(converted);
        } else {
          const stillHasLocalConversation = chatMessagesRef.current.some((entry) => entry.role === 'user');
          if (!stillHasLocalConversation) {
            setMessages([buildGreeting(welcomeMessage)]);
          }
        }
      } catch (err) {
        console.error('[ChatPanel] Failed to load messages:', err);
        const stillHasLocalConversation = chatMessagesRef.current.some((entry) => entry.role === 'user');
        if (!stillHasLocalConversation) {
          setMessages([buildGreeting(welcomeMessage)]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    void loadMessages();
  }, [buildGreeting, sessionId, setMessages, welcomeMessage]);

  useLayoutEffect(() => {
    scheduleScrollToBottom(true);
  }, [chatMessages.length, scheduleScrollToBottom]);

  useLayoutEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 140;
    const nextHeight = Math.max(30, Math.min(textarea.scrollHeight, maxHeight));
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    textarea.scrollTop = textarea.scrollHeight;
  }, [message]);

  useEffect(() => () => {
    if (pendingScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(pendingScrollFrameRef.current);
      pendingScrollFrameRef.current = null;
    }
    if (pendingStreamFrameRef.current !== null) {
      window.cancelAnimationFrame(pendingStreamFrameRef.current);
      pendingStreamFrameRef.current = null;
    }
  }, []);

  const handleListScroll: React.UIEventHandler<HTMLDivElement> = useCallback((event) => {
    const container = event.currentTarget;
    const distanceFromBottom = container.scrollHeight - (container.scrollTop + container.clientHeight);
    stickToBottomRef.current = distanceFromBottom <= 48;
  }, []);

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || sendingRef.current) {
      return;
    }

    const userMessage: ChatMsg = {
      id: createLocalMessageId(),
      role: 'user',
      content: trimmed,
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    };
    const assistantId = createLocalMessageId();
    const placeholder: ChatMsg = {
      id: assistantId,
      role: 'assistant',
      content: '',
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    };

    setMessage('');
    setSending(true);
    sendingRef.current = true;
    stickToBottomRef.current = true;
    currentAssistantIdRef.current = assistantId;
    streamedContentRef.current = '';
    setMessages((prev) => [...prev, userMessage, placeholder]);
    scheduleScrollToBottom(true);

    try {
      let response: ChatResponse | null = null;
      const onToken = (chunk: string) => {
        if (!chunk) {
          return;
        }
        streamedContentRef.current += chunk;
        flushStreamedContent(assistantId);
      };

      response = onChatMessage
        ? await onChatMessage(trimmed, { onToken })
        : await apiService.streamStrategyChat(trimmed, onToken);

      if (pendingStreamFrameRef.current !== null) {
        window.cancelAnimationFrame(pendingStreamFrameRef.current);
        pendingStreamFrameRef.current = null;
      }

      const finalText = streamedContentRef.current || response?.message || '';
      if (!finalText.trim()) {
        throw new Error('Received empty assistant response from stream');
      }
      setAssistantContent(assistantId, finalText);

      const resolvedSessionId = response?.session_id || apiService.getSessionId() || sessionId || undefined;
      if (resolvedSessionId) {
        void (async () => {
          await apiService.saveChatMessage('user', trimmed, resolvedSessionId);
          await apiService.saveChatMessage('assistant', finalText, resolvedSessionId);
        })().catch((err) => {
          console.error('[ChatPanel] Failed to persist chat messages:', err);
        });
      }
    } catch (err) {
      if (pendingStreamFrameRef.current !== null) {
        window.cancelAnimationFrame(pendingStreamFrameRef.current);
        pendingStreamFrameRef.current = null;
      }
      setAssistantContent(assistantId, 'Sorry, I encountered an error. Please try again.');
      console.error('Chat send failed', err);
    } finally {
      streamedContentRef.current = '';
      setSending(false);
      sendingRef.current = false;
      currentAssistantIdRef.current = null;
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!sendingRef.current) {
        void handleSend();
      }
    }
  };

  const clearChat = async () => {
    if (sendingRef.current) {
      return;
    }

    try {
      if (sessionId) {
        await apiService.clearChatHistory();
      }
    } catch (err) {
      console.error('[ChatPanel] Failed to clear backend chat history:', err);
    }

    setMessages([buildGreeting('New session started. How can I help?')]);
    stickToBottomRef.current = true;
    scheduleScrollToBottom(true);
  };

  return (
    <div className="chat-panel flex-1 min-h-0 max-h-full h-full overflow-hidden flex flex-col">
      <div ref={listRef} onScroll={handleListScroll} className="chat-panel__list flex-1 min-h-0 max-h-full overflow-y-auto p-3 space-y-1">
        {isLoading && (
          <div className="flex items-center gap-2 text-indigo-200 text-[12px]">
            <Loader2 className="w-4 h-4 text-indigo-200 ca-spin" />
            <span>Loading chat history...</span>
          </div>
        )}
        {chatMessages.map((msg) => {
          const isUser = msg.role === 'user';
          const isCurrentAssistant = !isUser && msg.id === currentAssistantIdRef.current;
          const showThinking = isCurrentAssistant && sending && !msg.content.trim();
          return (
            <div key={msg.id} className="flex w-full">
              <div className={`w-full flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`w-full ${
                    isUser ? 'ml-0 user-bubble' : 'mr-0 assistant-message'
                  } rounded-2xl chat-compact leading-relaxed`}
                  style={{ maxWidth: '100%' }}
                >
                  {isUser ? (
                    <p className="leading-relaxed">{msg.content}</p>
                  ) : showThinking ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-indigo-200 ca-spin" />
                      <span className="text-indigo-200">Thinking...</span>
                    </div>
                  ) : msg.content ? (
                    <ChatMarkdown text={msg.content} className="chat-markdown" />
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-indigo-800/30 p-3">
        <div className="flex items-end gap-2">
          <div className="relative flex-1">
            <textarea
              ref={inputRef}
              rows={1}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="ask astra (shfit + enter for newline)"
              className="w-full bg-white text-gray-900 placeholder-indigo-300 border border-indigo-700/50 rounded-lg px-3 py-1 pr-8 text-[12px] focus:outline-none focus:ring-2 focus:ring-cyan-500"
              style={{ resize: 'none', minHeight: '30px', lineHeight: '1.25' }}
              disabled={sending}
            />
            <span
              aria-hidden="true"
              className="absolute right-2 bottom-1.5 text-[11px] leading-none text-indigo-400 select-none pointer-events-none"
              title="Press Enter to send"
            >
              ↵
            </span>
          </div>
          <button
            onClick={() => {
              void clearChat();
            }}
            className={`bg-indigo-900 text-indigo-300 p-2 rounded-lg border border-indigo-700/50 ${sending ? 'opacity-50 cursor-not-allowed' : ''}`}
            title="Clear chat"
            disabled={sending}
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
